/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { 
  FETCH_BANK_ACCOUNTS, 
  SAVE_BANK_ACCOUNT, 
  UPDATE_CHEQUE_LEAF 
} from "../graphql";
import { VApplication } from "../../../application";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IBankAccountMethods extends IPluginMethodMap {
  fetchBankAccounts(args: any): Promise<any>;
  saveBankAccount(args: any): Promise<any>;
  updateChequeLeaf(args: any): Promise<any>;
}

export interface FetchBankAccountsProps {
  vuid: string;
}

export interface SaveBankAccountProps {
  accHolderName: string,
  accountNo: string,
  bankName: string,
  chequeLeaf: {
    doc: string,
    fileType: string,
    name: string,
    status: string,
    type: string
  },
  swiftCode: string,
  type: string
}

export interface UpdateChequeLeafProps {
  chequeLeafId: string,
  kycDocIds: string[],
  userId: string
}
export class BankAccountService implements IAgentPlugin {
  readonly methods: IBankAccountMethods;

  constructor() {
    this.methods = {
      fetchBankAccounts: this.fetchBankAccounts.bind(this),
      saveBankAccount: this.saveBankAccount.bind(this),
      updateChequeLeaf: this.updateChequeLeaf.bind(this),
    };
  }

  async fetchBankAccounts(args: FetchBankAccountsProps) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const res = await client.query({
          query: FETCH_BANK_ACCOUNTS,
          variables: {
            input: {
              ...args,
            },
          },
          fetchPolicy: "network-only",
        });
        if (res?.data?.getBankAccountDetail) {
          return {
            data: res?.data?.getBankAccountDetail,
          };
        }
      }
    } catch (err: any) {
      throw err;
    }
  }

  async saveBankAccount(args: SaveBankAccountProps) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const res = await client.mutate({
          mutation: SAVE_BANK_ACCOUNT,
          variables: {
            input: {
              ...args,
            },
          },
          fetchPolicy: "no-cache",
        });
        return {
          data: res?.data
        }
      }
    } catch (err: any) {
      throw err;
    }
  }

  async updateChequeLeaf(args: UpdateChequeLeafProps) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const res = await client.mutate({
          mutation: UPDATE_CHEQUE_LEAF,
          variables: {
            input: {
              ...args,
            },
          },
          fetchPolicy: "no-cache",
        });
        return {
          data: res?.data
        }
      }
    } catch (err: any) {
      throw err;
    }
  }
}
